<template>
    <div v-if="curOrder" class="order-items">
        <div ref="content">
            <div
                v-for="(item, index) in orderItems"
                :key="index"
                v-ripple
                class="item"
                :class="{
                    'selected': curOrderItem && curOrderItem.hash === item.hash,
                    'd-flex': item.getDiscountAmount().isZero() && !item.exempt_from_tax && !item.comments.length
                }"
                @click.stop.prevent="toggleMenu(item, $event)"
            >
                <v-row no-gutters>
                    <v-col
                        cols="9"
                        class="item-label"
                        :class="{'latest': index == 0}"
                    >
                        <div>
                            <span>
                                {{ item.getCount() + ' x&nbsp;&nbsp;&nbsp;' + item.name }}
                            </span>
                            <span class="item-unit-price">
                                ({{ item.getUnitPrice().toFormat() }})
                            </span>
                        </div>
                    </v-col>

                    <v-col cols="3" class="item-price">
                        {{ item.getPrice().toFormat('0.00') }}

                        <div class="item-menu">
                            <v-menu
                                v-model="showMenu[item.hash]"
                                class="ma-0"
                                bottom
                                transition="slide-y-transition"
                            >
                                <template v-slot:activator="{ on }">
                                    <v-btn
                                        v-on="on"
                                        icon
                                        small
                                        @click.stop.prevent="toggleMenu(item, $event)"
                                    >
                                        <v-icon>far fa-ellipsis-v</v-icon>
                                    </v-btn>
                                </template>

                                <v-list>
                                    <v-list-item
                                        :key="1"
                                        @click="toggleDialog(item, 'itemQuantity')"
                                    >
                                        <v-list-item-action class="mr-3">
                                            <v-icon>fal fa-sort-numeric-up</v-icon>
                                        </v-list-item-action>
                                        <v-list-item-title>{{ $t('general.quantity') }}</v-list-item-title>
                                    </v-list-item>

                                    <v-list-item
                                        :key="2"
                                        @click="toggleDialog(item, 'itemDiscount')"
                                    >
                                        <v-list-item-action class="mr-3">
                                            <v-icon>fal fa-percent</v-icon>
                                        </v-list-item-action>
                                        <v-list-item-title>{{ $t('general.discount') }}</v-list-item-title>
                                    </v-list-item>

                                    <v-list-item
                                        :key="3"
                                        :disabled="priceUpdateDisabled(item)"
                                        @click="toggleDialog(item, 'itemUnitPrice')"
                                    >
                                        <v-list-item-action class="mr-3">
                                            <v-icon :disabled="priceUpdateDisabled(item)">
                                                {{ Money.currency === 'EUR' ? 'fal fa-euro-sign' : 'fal fa-usd-circle' }}
                                            </v-icon>
                                        </v-list-item-action>
                                        <v-list-item-title>{{ $t('general.price') }}</v-list-item-title>
                                    </v-list-item>

                                    <v-list-item
                                        :key="4"
                                        @click="toggleDialog(item, 'itemComments')"
                                    >
                                        <v-list-item-action class="mr-3">
                                            <v-icon>fal fa-comment</v-icon>
                                        </v-list-item-action>
                                        <v-list-item-title>{{ $t('general.comment') }}</v-list-item-title>
                                    </v-list-item>

                                    <v-list-item
                                        :key="5"
                                        @click="exemptFromTax(item)"
                                    >
                                        <v-list-item-action class="mr-3">
                                            <v-icon>fal fa-badge-percent</v-icon>
                                        </v-list-item-action>
                                        <v-list-item-title>{{ $t('order.exempt_from_tax') }}</v-list-item-title>
                                    </v-list-item>

                                    <v-list-item
                                        :key="6"
                                        class="red--text text--darken-1"
                                        @click="deleteItem(item)"
                                    >
                                        <v-list-item-action class="mr-3">
                                            <v-icon class="red--text">fal fa-times</v-icon>
                                        </v-list-item-action>
                                        <v-list-item-title class="red--text">
                                            {{ $t('general.delete_item') }}
                                        </v-list-item-title>
                                    </v-list-item>
                                </v-list>
                            </v-menu>
                        </div>
                    </v-col>
                </v-row>

                <v-row
                    v-if="item.comments.length"
                    class="comment"
                    no-gutters
                >
                    <v-col cols="12">
                        <div class="comment-label" v-html="nl2br(item.comments[0])"></div>
                    </v-col>
                </v-row>

                <v-row
                    v-if="!item.getDiscountAmount().isZero()"
                    class="discount"
                    no-gutters
                >
                    <v-col cols="9">
                        <div class="discount-label">
                            <strong class="info--text">
                                {{ `${$t('general.discount')} -${item.getDiscountPercent()}% ` }}
                            </strong>
                            {{ `( ${item.getDiscountAmount().toFormat('0.00')} )` }}
                        </div>
                    </v-col>

                    <v-col cols="3">
                        <div class="discount-price">
                            {{ item.getToPay().toFormat('0.00') }}
                        </div>
                    </v-col>
                </v-row>

                <v-row
                    v-if="item.exempt_from_tax"
                    class="exempt-from-tax"
                    no-gutters
                >
                    <v-col cols="12">
                        <div class="exempt-from-tax-label">
                            <strong class="error--text">{{ $t('order.exempt_from_tax') }}</strong>
                            <small class="ml-2">{{ $t(`order.exempt_from_tax_reason.${item.exempt_from_tax.split('|')[0]}`) }}</small>
                        </div>
                    </v-col>
                </v-row>
            </div>
        </div>
    </div>
</template>

<script>
import * as mutationTypes from '@/store/mutationTypes';
import { mapGetters } from 'vuex';

export default {
    name: 'OrderItems',

    data: () => ({
        showMenu: {},
    }),

    watch: {
        curOrder: {
            deep: true,
            handler(val) {
                if(!val) {
                    this.showMenu = {};
                    return
                }

                let showMenu = {};
                val.items.forEach(item => {
                    showMenu[item.hash] = false;
                });

                this.showMenu = showMenu;
            }
        },

        show: {
            deep: true,
            handler(val) {
                this.eventBus.$emit('show-screens', val)
            }
        },
    },

    computed: {
        ...mapGetters(['curOrder', 'curOrderItem']),

        orderItems() {
            return this.curOrder ? this.curOrder.$duplicate().items.reverse() : []
        },

        inventoryAccountingEnabled() {
            return !!this.tenant.venue?.settings?.use_inventory_accounting;
        },

        inventoryAccountingPriceChangeEnabled() {
            return !!this.tenant.venue?.settings?.inventory_accounting_price_change_enabled;
        },
    },

    methods: {
        toggleDialog(item, dialog) {
            this.setSelectedItem(item);
            this.$emit('toggle-dialog', { dialog: dialog })
        },

        onItemSelect(item) {
            this.setSelectedItem(item);

            setTimeout(() => {
                if(this.$refs.content) {
                    this.$refs.content.scrollTop = 0
                }
            }, 250);
        },

        toggleMenu(item, event) {
            this.setSelectedItem(item);

            Object.keys(this.showMenu).forEach(hash => {
                if (hash !== item.hash) {
                    this.$set(this.showMenu, hash, false);
                }
            });

            this.$set(this.showMenu, item.hash, !this.showMenu[item.hash]);
        },

        deleteItem(item) {
            this.$emit('delete-item', { item: item });
        },

        setSelectedItem(item) {
            this.$store.commit(mutationTypes.SET_CUR_ORDER_ITEM, { curOrderItem: item });
        },

        exemptFromTax(item) {
            if(!['me', 'sr'].includes(locale)) {
                this.curOrder.setItemExemptFromTax(item, item.exempt_from_tax === 'VAT_EXCL' ? null : 'VAT_EXCL');
                return;
            }

            this.toggleDialog(item, 'itemExemptFromTax')
        },

        priceUpdateDisabled(item) {
            const product = item.getProduct();
            if(!product) return true;

            return !!product.is_goods && (this.inventoryAccountingEnabled && !this.inventoryAccountingPriceChangeEnabled);
        }
    }
};
</script>

<style lang="scss" scoped>
.order-items {

    .item {
        padding: 5px;
        min-height: 50px;
        align-items: center;
        border-bottom: 1px solid #e4e6ee;
        cursor: pointer;

        &.selected {
            background: #eceff7;
        }

        .item-label {
            flex: 1;
            display: flex;
            word-break: break-word;
            justify-content: center;
            flex-direction: column;

            &.latest {
                font-size: 26px;
                font-weight: 500;
                line-height: 1;
            }

            .item-unit-price {
                color: var(--v-error-base);
                text-wrap: nowrap;
            }
        }

        .item-price {
            display: flex;
            align-items: center;
            justify-content: flex-end;

            .menu {
                margin-right: -5px;
                width: 30px;
                display: flex;
                align-items: center;
                justify-content: flex-end;

                .v-btn {
                    color: #a7b1bb;
                    margin: 0;
                }
            }
        }

        .discount {
            margin-top: 5px;

            .discount-label {
                font-size: 15px;
                padding-left: 10px;
                margin-left: 20px;
                border-left: 2px solid #318cf5;
            }

            .discount-price {
                text-align: right;
                padding-right: 28px;
            }
        }

        .exempt-from-tax {
            margin-top: 5px;

            .exempt-from-tax-label {
                font-size: 15px;
                padding-left: 10px;
                margin-left: 20px;
                border-left: 2px solid #FF5252;
            }
        }

        .comment {
            margin-top: 5px;

            .comment-label {
                font-size: 15px;
                padding-left: 10px;
                margin-left: 20px;
                border-left: 2px solid #BF9002;
            }
        }
    }
}
</style>
